// HCP login button on the practitioners page. Reads customer info from data-*,
// posts to the Shopify app proxy to mint a JWT, then opens the practitioner
// portal, handing the token over via a hidden POST form. A genuine click opens
// the portal in a NEW tab — the tab is opened synchronously on click (inside the
// user gesture) so it survives the awaited fetch without being popup-blocked.
//
// Also auto-runs the same flow when the page loads with ?hcp=true — that flag is
// added to the login button's return_to URL, so a guest who logs in via the
// practitioner login button lands back here and is taken straight to the portal.
// That path is a script-dispatched click (no user gesture), so a new tab opens
// only if the visitor has allowed pop-ups for the site; otherwise it falls back
// to the same tab. It also only fires after a genuine login (hcp_pending guard).

const PORTAL_BASE_URL = 'https://practitioner-resources.com/';
const JWT_ENDPOINT = '/apps/my-custom-path/api/email/jwt/generate';
// Name of the portal tab, so a tab pre-opened on click can be targeted by the form.
const PORTAL_WINDOW_NAME = 'hcpPortal';

const hcpBtn = document.querySelector('.view_hcp_btn');

/**
 * @param {HTMLElement} btn - The HCP button carrying data-id/email/name.
 * @param {Window | null} [portalWindow] - Tab opened synchronously during a real
 *   click; when present the portal loads into it (new tab), else the same tab.
 */
async function openPractitionerPortal(btn, portalWindow = null) {
  const customer = {
    id: btn.dataset.id,
    email: btn.dataset.email,
    name: btn.dataset.name,
  };

  // Full-screen loader (shared theme overlay) while we mint the JWT and open the
  // portal — most visible on the ?hcp=true auto-redirect after login.
  const loader = document.querySelector('.loader_main');
  if (loader instanceof HTMLElement) loader.style.display = 'block';

  try {
    const response = await fetch(JWT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: customer.id,
        user_email: customer.email,
        user_login: customer.email,
        user_nicename: customer.name,
        user_url: '',
        user_registered: '',
        user_status: 0,
        display_name: customer.name,
        user_role: ['practitioner'],
      }),
    });

    const data = await response.json();
    const token = data?.result?.token;

    if (token) {
      // Hand the JWT to the partner portal via a hidden POST form rather than a URL
      // querystring, so the token never lands in the URL / Referer / partner access
      // logs. If a tab was pre-opened on the click, target it (new tab) — it was
      // created inside the user gesture so it survives this awaited fetch and isn't
      // popup-blocked. Otherwise (auto ?hcp=true open, or the popup was blocked) load
      // in the same tab.
      const useNewTab = !!(portalWindow && !portalWindow.closed);

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = PORTAL_BASE_URL;
      form.target = useNewTab ? PORTAL_WINDOW_NAME : '_self';
      form.style.display = 'none';

      const tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = 'token';
      tokenInput.value = token;
      form.appendChild(tokenInput);

      document.body.appendChild(form);
      form.submit();
      form.remove();
    } else if (portalWindow && !portalWindow.closed) {
      // No token minted — don't leave an empty tab hanging.
      portalWindow.close();
    }
  } catch {
    // Silent fail — user can retry; no telemetry endpoint wired yet.
    if (portalWindow && !portalWindow.closed) portalWindow.close();
  } finally {
    if (loader instanceof HTMLElement) loader.style.display = 'none';
  }
}

// Guest login button: mark that a genuine, click-initiated login round-trip is in
// progress, so the ?hcp=true auto-open below only fires after a real login — not on
// a bookmarked/shared ?hcp=true link or a history restore.
const hcpLoginLink = document.querySelector('[data-hcp-login]');
if (hcpLoginLink) {
  hcpLoginLink.addEventListener('click', () => {
    try {
      sessionStorage.setItem('hcp_pending', '1');
    } catch (e) {}
  });
}

if (hcpBtn instanceof HTMLElement) {
  hcpBtn.addEventListener('click', (event) => {
    event.preventDefault();
    // Always try to open the portal tab now, synchronously, so it survives the
    // awaited JWT fetch. A genuine click reliably opens it. On the script-
    // dispatched ?hcp=true auto-open there's no user gesture, so the browser
    // blocks the popup UNLESS the visitor has allowed pop-ups for the site — in
    // which case it still opens in a new tab. If blocked, window.open returns
    // null and openPractitionerPortal falls back to the same tab.
    const portalWindow = window.open('', PORTAL_WINDOW_NAME);
    openPractitionerPortal(hcpBtn, portalWindow);
  });

  // On returning from a genuine login with ?hcp=true, auto-click the HCP button to
  // open the portal. Gated on hcp_pending so a bookmarked/shared ?hcp=true URL (or
  // a history restore) doesn't auto-run the flow.
  const params = new URLSearchParams(window.location.search);
  let loginPending = false;
  try {
    loginPending = sessionStorage.getItem('hcp_pending') === '1';
  } catch (e) {}

  if (params.get('hcp') === 'true' && loginPending) {
    try {
      sessionStorage.removeItem('hcp_pending');
    } catch (e) {}

    hcpBtn.click();

    // Remove the flag so a refresh doesn't re-open the portal.
    params.delete('hcp');
    const query = params.toString();
    const cleanUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }
}
